import { DatePipe } from '@angular/common';
import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilKeyChanged, filter } from 'rxjs/operators';
import { ActionSheets } from '../../../constants/action-sheets.constants';
import { ClassNames } from '../../../constants/class-names.constants';
import { Color } from '../../../constants/color.constants';
import { DateFormat } from '../../../constants/date-format.constants';
import { Icons } from '../../../constants/icons.constants';
import {
    ActionSheet,
    DatePickerConfiguration,
    IOptionalExportServiceConfig,
    MobileBannerTabsConfig,
    NavigationItem
} from '../../../models/common';
import {
    FilterRequest,
    ListResponse,
    ProxyEndpointRequest,
    Response,
    SearchFilterRequest,
    VoidResponse
} from '@lsq-frontend/remote-services/enterprise/common';
import { ActionSheetService } from '../../../services/action-sheet/action-sheet.service';
import { ApiService } from '@lsq-frontend/angular-shared/api-service';
import { BackdropService } from '../../../services/backdrop/backdrop.service';
import { FloatingActionButtonService } from '../../../services/floating-action-button/floating-action-button.service';
import { ResponsiveService } from '../../../services/responsive/responsive.service';
import { WindowReferenceService } from '../../../services/window-reference/window-reference.service';
import { forLifeOf, LifecycleAware } from '../../../utilities/rxjs.helpers';
import { Utilities } from '../../../utilities/utilities.helper';
import { LsqSearchComponent } from '../../forms/search/lsq-search.component';
import { IDateRange } from '../date-picker/models/date-range';

/**
 *
 * @export
 * @class LsqBannerComponent
 *
 * @property {string} [titleText] Add a title to the banner.
 * @property {string} [subTitle] Add text below the title, such as a 'last updated' timestamp.
 * @property {string} [breadcrumbRoute] The route you would like breadcrumb to return to.
 * @property {string} [breadcrumb] The text of the breadcrumb you want to display.
 * @property {string} [background] If set to "white" then the background will be white. Defaults to gray.
 * @property {boolean} [hasContent] If `true`, `ng-content` is inserted to the left of the right side icons.
 * @property {string=} [contentSide] Determines which side to render child content
 * @property {string} [reportName] The name of the report. May act as the title if no title is provided.
 * @property {string} [analyticsName] The key associated with the parent report in analytics.
 * @property {any[]} [selectOptions] Add a dropdown with the provided options.
 * @property {DatePickerConfiguration} [datePickerConfiguration] Date picker configuration, if present it displays a datepicker
 * @property {Observable<NavigationItem[]>} [navigationItemsStream] Routes for the tab bar.
 * @property {ApiService<FilterRequest, ListResponse<any>>} [apiService] The service to pass to the export form.
 * @property {TemplateRef<any>} [filterDrawerTemplate] The template contents of the filter drawer.
 * @property {object} [filterDrawerContext] The context passed to the `filterDrawerTemplate`.
 * @property {boolean} [filterDrawerOpen] The open/close state of the filter drawer.
 * @property {EventEmitter} [filterDrawerOpenChange] Provides two-way binding for `filterDrawerOpen`.
 * @property {boolean} [includeDateFilterStringPlaceholder] If `false`, removes placeholder on pages without date filter
 *
 * @todo: Add lsq-select dropdown
 * @todo: Wire up the endpoints for searching
 */
@LifecycleAware()
@Component({
    selector: 'lsq-banner',
    template: `
        <div
            class="banner"
            [ngClass]="
                []
                    | cacheValue
                        : getContainerClassList
                        : stackEagerly
                        : background
                        : parentClass
                        : mobileBannerTabsConfig
                        : isMobile
                        : datePickerConfiguration
                        : bottomBorderColor
                        : bottomBorderCondition
            "
        >
            <div
                class="banner-main container-fluid page-container"
                [ngClass]="{
                    'has-right-side-content': hasContent && contentSide === 'right'
                }"
            >
                <div
                    class="banner-left"
                    [ngClass]="{
                        'title-only': !subTitle && !showDateSelectMobileToggleIcon(),
                        searchable: search,
                        'search-open': isSearchOpen
                    }"
                >
                    <div class="banner-breadcrumb" *ngIf="breadcrumb">
                        <h6 *ngIf="breadcrumbRoute">
                            <a [routerLink]="breadcrumbRoute"
                                ><lsq-icon
                                    [type]="icons.LeftArrowSmall"
                                    [color]="color.Green"
                                ></lsq-icon>
                                {{ breadcrumb }}</a
                            >
                        </h6>
                        <h6 *ngIf="!breadcrumbRoute && breadcrumb">
                            <lsq-icon
                                [type]="icons.LeftArrowSmall"
                                [color]="color.Green"
                            ></lsq-icon>
                            {{ breadcrumb }}
                        </h6>
                    </div>
                    <div class="banner-action-icon-container" *ngIf="search && !isMobile">
                        <lsq-button
                            id="switch-customer-button"
                            appearance="primary"
                            (click)="toggleSearch()"
                        >
                            <lsq-icon [type]="icons.SwitchCustomers" [color]="color.White">
                            </lsq-icon>
                            <div>Switch</div>
                        </lsq-button>
                    </div>
                    <div class="banner-left-title-container">
                        <h1
                            *ngIf="(titleText || reportName) && !isSearchOpen"
                            [ngClass]="{
                                multiline: mobileTitleMultiline,
                                'pad-right': hasContent && contentSide === 'right',
                                'text-truncate': truncateTitle
                            }"
                            (click)="toggleSearch()"
                        >
                            {{ !!titleText ? titleText : reportName }}
                        </h1>
                        <div class="customer-switch" *ngIf="isSearchOpen">
                            <form [formGroup]="formGroup">
                                <lsq-search
                                    #customerSearch
                                    name="customerSearch"
                                    placeholder="Search Customers"
                                    primaryKey="debtorKey"
                                    primaryDisplayKey="debtorName"
                                    secondaryDisplayKey="address"
                                    formControlName="customerSearchControl"
                                    [searchService]="searchService"
                                    [group]="formGroup"
                                    [alwaysShowClose]="true"
                                    [largeInlineStyling]="true"
                                    (clickOutside)="toggleSearch()"
                                    [allowIneligible]="true"
                                    exclude="#switch-customer-button,.searchable h1"
                                >
                                </lsq-search>
                            </form>
                        </div>
                        <p class="text-bold mobile-date" *ngIf="showDateSelectMobileToggleIcon()">
                            {{ getDateFilterString() }}
                        </p>
                        <p
                            class="text-bold"
                            *ngIf="
                                includeDateFilterStringPlaceholder &&
                                !showDateSelectMobileToggleIcon() &&
                                subTitle &&
                                isMobile
                            "
                        >
                            &nbsp;<!-- place holder so that banner doesn't jump around on non date filtered pages on mobile -->
                        </p>
                        <p
                            *ngIf="!isSearchOpen && (subTitle || subTitle === '')"
                            id="banner-subtitle"
                            [ngClass]="{ 'banner-subtitle': !isMobile }"
                        >
                            {{ subTitle }}
                            <span *ngIf="subTitle === ''">&nbsp;</span>
                        </p>
                    </div>
                    <ng-container *ngIf="hasContent && contentSide === 'left' && !isSearchOpen">
                        <ng-content></ng-content>
                    </ng-container>
                </div>

                <div
                    class="banner-right"
                    [ngClass]="{ 'content-fits': contentFits }"
                    *ngIf="
                        showExportIcon() ||
                        (hasContent && contentSide === 'right') ||
                        datePickerConfiguration
                    "
                >
                    <ng-container *ngIf="hasContent && contentSide === 'right'">
                        <ng-content></ng-content>
                    </ng-container>

                    <div *ngIf="datePickerConfiguration && !isMobile">
                        <ng-container *ngTemplateOutlet="dateSelectTemplate"></ng-container>
                    </div>

                    <lsq-select
                        *ngIf="selectOptions?.length > 0"
                        [name]="'banner-select'"
                        [options]="selectOptions"
                    >
                    </lsq-select>

                    <ul *ngIf="showBannerIcons()" class="banner-icons">
                        <li *ngIf="showExportIcon()" class="banner-icon export">
                            <lsq-export-form
                                [reportName]="reportName"
                                [analyticsName]="analyticsName"
                                [service]="apiService"
                                [optionalExportApiServiceConfig]="optionalExportApiServiceConfig"
                                [shareService]="shareService"
                                [shareServiceFeatureFlag]="shareServiceFeatureFlag"
                            >
                            </lsq-export-form>
                        </li>

                        <li
                            *ngIf="showDateSelectMobileToggleIcon()"
                            class="lsq-date-select-mobile-toggle"
                        >
                            <lsq-button
                                ariaLabel="Toggle date selector"
                                appearance="transparent"
                                (buttonClick)="toggleDateSelectMobileDrawer()"
                            >
                                <lsq-icon [type]="icons.Calendar" [color]="color.Green"></lsq-icon>
                            </lsq-button>
                        </li>
                    </ul>
                </div>
            </div>

            <div *ngIf="navigationItemsStream">
                <div class="banner-sub-tabs container-fluid page-container">
                    <div class="banner-tabs">
                        <lsq-banner-tabs
                            [navigationItemsStream]="navigationItemsStream"
                        ></lsq-banner-tabs>
                    </div>
                </div>
            </div>
        </div>

        <div *ngIf="filterDrawerOpen" class="banner-drawer banner-drawer-filter">
            <div class="container-fluid page-container">
                <ng-container
                    *ngTemplateOutlet="
                        filterDrawerTemplate;
                        context: {
                            $implicit: filterDrawerContext
                        }
                    "
                >
                </ng-container>
            </div>
        </div>

        <div *ngIf="isMobile && mobileBannerTabsConfig?.tabs?.length > 0">
            <lsq-mobile-banner-tabs [config]="mobileBannerTabsConfig"></lsq-mobile-banner-tabs>
        </div>

        <ng-template #dateSelectTemplate>
            <lsq-date-select
                *ngIf="!!datePickerConfiguration && !hideDatePicker"
                [type]="datePickerConfiguration.type"
                [sideBySide]="datePickerConfiguration.sideBySide"
                [calendarLabel]="datePickerConfiguration.calendarLabel"
                [allowFuture]="datePickerConfiguration.allowFuture"
                [lowerLimit]="datePickerConfiguration.lowerLimit"
                [upperLimit]="datePickerConfiguration.upperLimit"
                (ngModelChange)="
                    datePickerConfiguration.modelChange &&
                        datePickerConfiguration.modelChange($event)
                "
                [(ngModel)]="datePickerConfiguration.model"
            >
            </lsq-date-select>
        </ng-template>
    `,
    styleUrls: ['./lsq-banner.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class LsqBannerComponent implements OnInit, OnDestroy {
    @ViewChild('dateSelectTemplate', { static: true }) public dateSelectTemplateRef: TemplateRef<
        any
    >;
    @ViewChild('customerSearch') public customerSearch: LsqSearchComponent;
    @Input() public titleText?: string;
    @Input() public subTitle?: string;
    @Input() public parentClass = '';
    @Input() public breadcrumbRoute?: string;
    @Input() public breadcrumb?: string;
    @Input() public background?: string;
    @Input() public hasContent = false;
    @Input() public contentSide?: 'left' | 'right' = 'right';
    @Input() public reportName?: string;
    @Input() public analyticsName?: string;
    @Input() public selectOptions?: any[];
    @Input() public datePickerConfiguration?: DatePickerConfiguration;
    @Input() public navigationItemsStream?: Observable<NavigationItem[]>;
    @Input('service') public apiService?: ApiService<FilterRequest, ListResponse<any>>;
    @Input() public optionalExportApiServiceConfig?: IOptionalExportServiceConfig;
    @Input() public filterDrawerTemplate?: TemplateRef<any>;
    @Input() public filterDrawerContext?: object;
    @Input() public shareService: ApiService<ProxyEndpointRequest, VoidResponse>;
    @Input() public shareServiceFeatureFlag: number;
    @Input() public mobileBannerTabsConfig?: MobileBannerTabsConfig;
    @Input() public mobileTitleMultiline?: boolean;
    @Input() public search?: boolean;
    @Input() public searchService?: ApiService<SearchFilterRequest, Response>;
    @Input() public requestSearchTextFieldName?: string;
    @Input() public hideExport = false;
    @Input() public hideDatePicker = false;
    @Input() public includeDateFilterStringPlaceholder = true;
    @Input() public stackEagerly = false;
    @Input() public bottomBorderColor?: Color;
    @Input() public bottomBorderCondition = false;
    @Input() public truncateTitle = false;

    // This is for when the banner fits the screen correctly at medium sizes, it will allow the right banner content to align correctly.
    @Input() public contentFits = false;

    @Output() public filterDrawerOpenChange = new EventEmitter<boolean>();
    @Output() public newCustomerSelected = new EventEmitter<number>();

    public isSearchOpen = false;
    public isMobile = false;
    public dateSelectMobileDrawerIsOpen = false;
    public formGroup: FormGroup;
    public dateSelectMobileActionSheet: ActionSheet;
    private _filterDrawerIsOpen = false;
    private _searchSubscription: Subscription;
    public icons = Icons;
    public color = Color;

    constructor(
        private responsiveService: ResponsiveService,
        private actionSheetService: ActionSheetService,
        public datePipe: DatePipe,
        public backdropService: BackdropService,
        public windowReferenceService: WindowReferenceService,
        public floatingActionButtonService: FloatingActionButtonService,
        public router: Router
    ) {
        this.router.events
            .pipe(
                forLifeOf(this),
                filter((e) => e instanceof NavigationEnd)
            )
            .subscribe(() => {
                this._addSwitchFab();
            });
    }

    @Input() public get filterDrawerOpen(): boolean {
        return this._filterDrawerIsOpen;
    }
    public set filterDrawerOpen(value: boolean) {
        this._filterDrawerIsOpen = value;
        this.filterDrawerOpenChange.emit(this._filterDrawerIsOpen);
    }

    public ngOnInit(): void {
        this.responsiveService.smBelow.pipe(forLifeOf(this)).subscribe((isMobile) => {
            this.isMobile = isMobile;
            if (this.isMobile && this.isSearchOpen) {
                this.backdropService.openBackdrop();
            }

            if (!this.isMobile && this.isSearchOpen) {
                this.backdropService.closeBackDrop();
            }
        });

        this.formGroup = new FormGroup({
            customerSearchControl: new FormControl(null, [])
        });
    }

    public ngAfterViewInit(): void {
        this.dateSelectMobileActionSheet = new ActionSheet({
            id: ActionSheets.DatePickerMobile,
            templateRef: this.dateSelectTemplateRef
        });
        this._addSwitchFab();
    }

    public ngOnDestroy(): void {}

    private _addSwitchFab(): void {
        // Remove the FAB so that "this" is correct if re-added.
        this.floatingActionButtonService.removeFab('customerSwitch');
        if (this.search) {
            this.floatingActionButtonService.addAction({
                identifier: 'customerSwitch',
                icon: this.icons.SwitchCustomers,
                action: this.toggleSearch.bind(this)
            });
        }
    }

    public getContainerClassList = (
        _initialValue: string[],
        stackEagerly: boolean,
        background: string,
        parentClass: string,
        mobileBannerTabsConfig: MobileBannerTabsConfig,
        isMobile: boolean,
        datePickerConfiguration: DatePickerConfiguration,
        bottomBorderColor: Color,
        bottomBorderCondition: boolean
    ): string[] => {
        const classList = [
            background === Color.White ? ClassNames.BannerWhite : ClassNames.BannerLightGray
        ];

        if (bottomBorderColor && bottomBorderColor === Color.Green && bottomBorderCondition) {
            classList.push(ClassNames.GreenBorderBottom);
        }

        if (parentClass) {
            classList.push(parentClass);
        }

        if (mobileBannerTabsConfig && isMobile) {
            if (mobileBannerTabsConfig.tabs && mobileBannerTabsConfig.tabs.length > 0) {
                classList.push(ClassNames.MobileBannerWithTabs);
            }
        }

        if (!!datePickerConfiguration) {
            classList.push(ClassNames.MobileBannerDateFilter);
        }

        if (stackEagerly) {
            classList.push(ClassNames.StackEagerly);
        }

        return classList;
    };

    public toggleFilterDrawer(): void {
        this.filterDrawerOpen = !this.filterDrawerOpen;
    }

    public toggleSearch(): void {
        if (!this.search) {
            this.isSearchOpen = false;
            this.backdropService.closeBackDrop();
            return;
        }
        this.isSearchOpen = !this.isSearchOpen;

        if (this.isSearchOpen) {
            if (this.isMobile) {
                this.windowReferenceService.scrollToTop();
                this.backdropService.openBackdrop();
            }
            if (this._searchSubscription) {
                this._searchSubscription.unsubscribe();
            }
            this.formGroup = new FormGroup({
                customerSearchControl: new FormControl(null, [])
            });
            this._searchSubscription = this.formGroup.valueChanges
                .pipe(forLifeOf(this), distinctUntilKeyChanged('customerSearchControl'))
                .subscribe((value) => {
                    if (value.customerSearchControl) {
                        this.newCustomerSelected.emit(value.customerSearchControl);
                        this.toggleSearch();
                    } else {
                        this.toggleSearch();
                    }
                });

            Utilities.executeAfterRender(() => {
                if (this.customerSearch && this.customerSearch.input) {
                    this.customerSearch.input.inputElement.nativeElement.focus();
                }
            });
        } else {
            this.backdropService.closeBackDrop();
            if (this._searchSubscription) {
                this._searchSubscription.unsubscribe();
            }
            this.formGroup.controls.customerSearchControl.setValue('');
        }
    }

    public getDateFilterString(): string {
        if (this.datePickerConfiguration && this.datePickerConfiguration.model) {
            const transform = (value: Date) =>
                this.datePipe.transform(value, DateFormat.LsqShortYear);
            if (!!(this.datePickerConfiguration.model as IDateRange).start) {
                return `${
                    transform((this.datePickerConfiguration.model as IDateRange).start as Date) ||
                    ''
                }
                    - ${
                        transform((this.datePickerConfiguration.model as IDateRange).end as Date) ||
                        ''
                    }`;
            } else {
                return `${transform(this.datePickerConfiguration.model as Date) || ''}`;
            }
        }
        return '';
    }

    public toggleDateSelectMobileDrawer(): void {
        this.actionSheetService.showActionSheet(this.dateSelectMobileActionSheet);
    }

    public showExportIcon(): boolean {
        return !this.isMobile && !!this.reportName && !!this.apiService && !this.hideExport;
    }

    public showDateSelectMobileToggleIcon(): boolean {
        return this.isMobile && !!this.datePickerConfiguration;
    }

    public showBannerIcons(): boolean {
        return this.showExportIcon() || this.showDateSelectMobileToggleIcon();
    }
}
